import styles from 'components/atoms/inputs/DatePicker/Calendar/Calendar.styl'
import { useCalendar, CalendarProps as AriaCalendarProps } from '@react-aria/calendar'
import { CalendarDate, createCalendar, DateValue, getLocalTimeZone, isSameDay, today } from '@internationalized/date'
import { useCalendarState } from '@react-stately/calendar'
import { useLocale } from '@react-aria/i18n'
import { FC, useCallback, useEffect, useMemo, useRef } from 'react'
import { CalendarGrid } from 'components/atoms/inputs/DatePicker/Calendar/CalendarGrid'
import { CalendarButton } from 'components/atoms/inputs/DatePicker/Calendar/CalendarButton'
import Button from 'components/features/pfm/Button/Button'
import Flex from 'components/atoms/Flex/Flex'
import { usePrev } from 'hooks/usePrev'

type CalendarProps =
  & AriaCalendarProps<DateValue>
  & {
    highlightedDates?: CalendarDate[]
    onTodayClick?: () => void
  }

export const Calendar: FC<CalendarProps> = (props) => {
  const { onTodayClick, highlightedDates, ...otherProps } = props
  const { locale } = useLocale()
  const state = useCalendarState({ ...otherProps, locale, createCalendar })
  const { isCellFocused, setFocusedDate } = state
  const selectedDate = state.value
  const calendar = useCalendar(otherProps, state)
  const { calendarProps, prevButtonProps, nextButtonProps, title } = calendar
  const ref = useRef(null)
  const now = today(getLocalTimeZone())
  const handleSetToday = useCallback(() => {
    !!onTodayClick && onTodayClick()
    state.selectDate(now)
    state.setValue(now)
    state.setFocusedDate(now)
  }, [now, onTodayClick, state])

  const previousDate = usePrev(selectedDate)
  useEffect(() => {
    const dateIsFocused = isCellFocused(selectedDate)
    const dateChanged = !isSameDay(previousDate, selectedDate)
    if (dateChanged && !dateIsFocused) {
      setFocusedDate(selectedDate)
    }
  }, [isCellFocused, previousDate, selectedDate, setFocusedDate])

  const todayButtonChild = useMemo(() => !!onTodayClick && (
    <Button
      text='Today'
      onClick={ handleSetToday }
      style='textOnly'
      size='mini'
      className={ styles.todayButton }
    />
  ), [handleSetToday, onTodayClick])

  return (
    <div { ...calendarProps } ref={ ref } className={ styles.calendar }>
      <Flex horizontal gap='spread' verticallyCenter>
        <CalendarButton { ...prevButtonProps } />
        <h3>{ title }</h3>
        <CalendarButton { ...nextButtonProps } />
      </Flex>
      { todayButtonChild }
      <CalendarGrid state={ state } highlightedDates={ highlightedDates } />
    </div>
  )
}
